import React, { useEffect, useMemo, useState, useCallback } from "react";
import { client } from "@/lib/api";
import { EventCard } from "@/design-system";
import { PUBLISHED } from "@/constants/testIds";
import { toast } from "sonner";
import { Search, Pencil, X } from "lucide-react";
import { confirm } from "@/lib/dialog";
import { formatDateLine, toNairobiInput, fromNairobiInput } from "@/lib/datetime";
import { PublishedPosterEditor } from "@/components/admin/PublishedPosterEditor";
import { InternetSourcesEditor } from "@/components/admin/InternetSourcesEditor";
import { coverSrc } from "@/lib/coverSrc";
import { PastedCoverPreview } from "@/components/admin/PastedCoverPreview";

// WW-160 (Vania 13 Oct · approved mockup) · one-line filter row (same
// pattern as the review queue, R27), yellow edit button per card, right
// drawer that saves in place + shows audit footer.  All times EAT
// (WW-162 helper).
const TAB_LABELS = { upcoming: "upcoming", on_now: "on now", past: "past", our_pick: "our pick" };
const IKO_OPTIONS = ["sherehe", "polite", "michezo", "kazi"];
const SORT_OPTIONS = [
  { k: "soonest", lbl: "soonest first" },
  { k: "newest_published", lbl: "newest published" },
  { k: "oldest_published", lbl: "oldest published" },
];
const MISSING_OPTIONS = ["venue", "price", "ticket_link", "description", "poster", "curator"];
const WHEN_OPTIONS = [
  { k: "this_week", lbl: "this week" },
  { k: "this_weekend", lbl: "this weekend" },
  { k: "next_week", lbl: "next week" },
  { k: "date", lbl: "a date" },
];
const menuStyle = { background: "rgba(18,18,42,0.72)", borderColor: "var(--line-night)", color: "var(--text-night)" };
const inputStyle = { marginTop: 6, display: "block", width: "100%", height: 36, padding: "0 10px", background: "rgba(18,18,42,0.72)", border: "1px solid var(--line-night)", borderRadius: 8, color: "var(--text-night)", fontSize: 13, colorScheme: "dark" };

// WW-160.7 · preview has several rows sharing one id; show each id once.
const uniqueById = (rows) => {
  const seen = new Set();
  return rows.filter((r) => (seen.has(r.id) ? false : seen.add(r.id)));
};

function pill(active) {
  return {
    background: active ? "var(--kili)" : "rgba(18,18,42,0.72)",
    borderColor: active ? "var(--kili)" : "var(--line-night)",
    color: active ? "#1A1A30" : "var(--text-night)",
  };
}

export default function Published() {
  const [events, setEvents] = useState([]);
  const [counts, setCounts] = useState({ upcoming: 0, on_now: 0, past: 0, our_pick: 0, all: 0 });
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("upcoming");
  const [iko, setIko] = useState("");
  const [source, setSource] = useState("");
  const [missing, setMissing] = useState("");
  const [sort, setSort] = useState("soonest");
  const [when, setWhen] = useState("");
  const [whenDate, setWhenDate] = useState("");
  const [editing, setEditing] = useState(null); // event being edited

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    params.set("tab", tab); params.set("sort", sort);
    if (when === "date" && whenDate) params.set("when", whenDate);
    else if (when && when !== "date") params.set("when", when);
    if (iko) params.set("iko", iko);
    if (source) params.set("source", source);
    if (missing) params.set("missing_info", missing);
    if (q.trim()) params.set("q", q.trim());
    const r = await client.get(`/admin/events?${params.toString()}`);
    setEvents(uniqueById(r.data.events || []));
    if (r.data.counts) setCounts(r.data.counts);
  }, [tab, iko, source, missing, sort, q, when, whenDate]);
  useEffect(() => { load(); }, [load]);

  const unpublish = async (id) => {
    if (!(await confirm({ title: "unpublish this event?", confirmLabel: "unpublish", tone: "danger" }))) return;
    await client.post(`/admin/events/${id}/unpublish`);
    toast.success("Unpublished"); await load();
  };

  return (
    <div className="p-8" style={{ background: "var(--bg-night-sunken)", minHeight: "100vh" }}>
      <p className="label-tiny" style={{ color: "var(--text-dim-night)" }}>live on watuwako.com</p>
      <h1 className="text-3xl lowercase" style={{ fontFamily: "var(--f-head)", fontWeight: 800, color: "var(--text-night)", letterSpacing: "-0.035em" }}>published events</h1>

      {/* search */}
      <div className="mt-5 relative" style={{ maxWidth: 480 }}>
        <span style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "var(--text-dim-night)" }}><Search size={16} /></span>
        <input
          data-testid={PUBLISHED.search || "published-search"}
          value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="search event, curator, or venue"
          style={{ width: "100%", height: 44, padding: "0 16px 0 40px", background: "rgba(18,18,42,0.72)", border: "1px solid var(--line-night)", borderRadius: 12, color: "var(--text-night)", fontFamily: "var(--f-body)", fontSize: 14, outline: "none" }}
        />
      </div>

      {/* WW-160.1 · one-line filter row: tabs + group menus + sort */}
      <div className="mt-5 flex items-center gap-2 overflow-x-auto whitespace-nowrap pb-1" data-testid="published-filter-row" style={{ fontFamily: "var(--f-head)", fontSize: 12 }}>
        {Object.entries(TAB_LABELS).map(([k, lbl]) => (
          <button key={k} data-testid={`published-tab-${k}`} onClick={() => setTab(k)}
            className="rounded-md border px-3 py-1 lowercase font-semibold" style={pill(tab === k)}>
            {lbl} <span style={{ opacity: 0.7 }}>·&nbsp;{counts[k] || 0}</span>
          </button>
        ))}
        <span className="opacity-50" style={{ color: "var(--text-dim-night)" }}>|</span>
        <select data-testid="published-filter-when" value={when} onChange={(e) => setWhen(e.target.value)}
          className="rounded-md border px-2 py-1 lowercase" style={menuStyle}>
          <option value="">when · any</option>
          {WHEN_OPTIONS.map((w) => <option key={w.k} value={w.k}>{w.lbl}</option>)}
        </select>
        {when === "date" && (
          <input type="date" data-testid="published-filter-when-date" value={whenDate} onChange={(e) => setWhenDate(e.target.value)}
            className="rounded-md border px-2 py-1" style={{ ...menuStyle, colorScheme: "dark" }} />
        )}
        <select data-testid="published-filter-iko" value={iko} onChange={(e) => setIko(e.target.value)}
          className="rounded-md border px-2 py-1 lowercase" style={{ background: "rgba(18,18,42,0.72)", borderColor: "var(--line-night)", color: "var(--text-night)" }}>
          <option value="">iko · any</option>
          {IKO_OPTIONS.map((i) => <option key={i} value={i}>{i}</option>)}
        </select>
        <select data-testid="published-filter-source" value={source} onChange={(e) => setSource(e.target.value)}
          className="rounded-md border px-2 py-1 lowercase" style={{ background: "rgba(18,18,42,0.72)", borderColor: "var(--line-night)", color: "var(--text-night)" }}>
          <option value="">source · any</option>
          <option value="manual">manual</option>
          <option value="drive">drive</option>
          <option value="valley_scout">valley_scout</option>
        </select>
        <select data-testid="published-filter-missing" value={missing} onChange={(e) => setMissing(e.target.value)}
          className="rounded-md border px-2 py-1 lowercase" style={{ background: "rgba(18,18,42,0.72)", borderColor: "var(--line-night)", color: "var(--text-night)" }}>
          <option value="">missing info · none</option>
          {MISSING_OPTIONS.map((m) => <option key={m} value={m}>{m === "ticket_link" ? "no ticket link" : `missing ${m}`}</option>)}
        </select>
        <span className="opacity-50" style={{ color: "var(--text-dim-night)" }}>|</span>
        <select data-testid="published-sort" value={sort} onChange={(e) => setSort(e.target.value)}
          className="rounded-md border px-2 py-1 lowercase" style={{ background: "rgba(18,18,42,0.72)", borderColor: "var(--line-night)", color: "var(--text-night)" }}>
          {SORT_OPTIONS.map((s) => <option key={s.k} value={s.k}>{s.lbl}</option>)}
        </select>
      </div>

      {events.length === 0 && (
        <p className="mt-10 text-sm" style={{ color: "var(--text-muted-night)", fontFamily: "var(--f-body)" }}>no events match these filters.</p>
      )}

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {events.map((e, idx) => (
          <div key={`${e.id}-${idx}`} data-testid={PUBLISHED.card(e.id)} className="relative" style={{ fontSize: 13 }}>
            <EventCard event={e} />
            <div className="absolute right-2 top-2 z-10 flex flex-col gap-1">
              <button
                data-testid={`published-edit-${e.id}`}
                onClick={() => setEditing(e)}
                className="rounded-md border px-2 py-1 text-[11px] font-semibold lowercase inline-flex items-center gap-1"
                style={{ background: "var(--kili)", borderColor: "var(--kili)", color: "#1A1A30", fontFamily: "var(--f-head)" }}
              >
                <Pencil size={11} /> edit
              </button>
              <button
                data-testid={`day-pick-${e.id}`}
                onClick={async () => {
                  try { await client.post(`/admin/events/${e.id}/day-pick`, { is_day_pick: !e.is_day_pick }); toast.success(e.is_day_pick ? "cleared" : "set as our pick"); await load(); }
                  catch { toast.error("failed"); }
                }}
                className="rounded-md border px-2 py-1 text-[11px] font-semibold lowercase"
                style={{ background: e.is_day_pick ? "var(--kili)" : "rgba(18,18,42,0.72)", borderColor: e.is_day_pick ? "var(--kili)" : "var(--line-night)", color: e.is_day_pick ? "#1A1A30" : "var(--text-night)", fontFamily: "var(--f-head)" }}>
                {e.is_day_pick ? "★ our pick" : "make our pick"}
              </button>
              <button
                data-testid={PUBLISHED.unpublishButton(e.id)}
                className="rounded-md border px-2 py-1 text-[11px] font-semibold lowercase"
                style={{ background: "rgba(18,18,42,0.72)", borderColor: "var(--line-night)", color: "var(--text-night)", fontFamily: "var(--f-head)" }}
                onClick={() => unpublish(e.id)}>unpublish</button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <EditDrawer
          event={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); }}
        />
      )}
    </div>
  );
}


function EditDrawer({ event, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: event.title || "", start_at: toNairobiInput(event.start_at), end_at: toNairobiInput(event.end_at),
    venue_display: event.venue_display || "", price: event.price || "",
    ticket_link: event.ticket_link || "", description: event.description || "",
    organiser_handle: event.organiser_handle || "",
    co_organiser_handles: (event.co_organiser_handles || []).join(", "),
    iko: event.iko || "sherehe", iko_supporting: (event.iko_supporting || []).slice(0, 2),
    cover_image_url: event.cover_image_url || "",
    slots: (event.slots || []).map((s) => ({ ...s, start_at: toNairobiInput(s.start_at), end_at: toNairobiInput(s.end_at) })),
  });
  const [audit, setAudit] = useState([]);
  const [saving, setSaving] = useState(false);
  const [posterOpen, setPosterOpen] = useState(false);
  const [posterConfirmed, setPosterConfirmed] = useState(true);
  const field = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const posterChanged = form.cover_image_url !== (event.cover_image_url || "");
  const posterBlocked = posterChanged && !posterConfirmed;
  const onPosterChange = useCallback((url, confirmed) => {
    setPosterConfirmed(confirmed);
    if (url) setForm((f) => ({ ...f, cover_image_url: url }));
  }, []);

  useEffect(() => {
    client.get(`/admin/events/${event.id}/audit`).then((r) => setAudit(r.data.audit || []));
  }, [event.id]);

  // WW-160.8 · the exact line the member event page prints (WW-161 rules).
  const membersWillSee = useMemo(() => {
    if (!form.start_at) return "date TBA";
    const extra = form.slots.filter((s) => s.start_at).length;
    const line = formatDateLine(fromNairobiInput(form.start_at), fromNairobiInput(form.end_at) || null);
    return extra ? `${line} · plus ${extra} more date${extra > 1 ? "s" : ""}` : line;
  }, [form.start_at, form.end_at, form.slots]);

  const gates = useMemo(() => {
    const g = [];
    if (!form.cover_image_url) g.push("poster missing");
    if (posterBlocked) g.push("new poster not confirmed");
    if (!form.venue_display) g.push("venue missing");
    if (!form.description) g.push("description missing");
    if (!form.organiser_handle) g.push("curator missing");
    return g;
  }, [form, posterBlocked]);

  const save = async () => {
    setSaving(true);
    try {
      const patch = {
        ...form,
        start_at: fromNairobiInput(form.start_at),
        end_at: fromNairobiInput(form.end_at),
        slots: form.slots.filter((s) => s.start_at).map((s) => ({ ...s, start_at: fromNairobiInput(s.start_at), end_at: fromNairobiInput(s.end_at) })),
        co_organiser_handles: form.co_organiser_handles.split(",").map((s) => s.trim()).filter(Boolean),
      };
      let r;
      try {
        r = await client.put(`/admin/events/${event.id}`, patch);
      } catch (pe) {
        const pd = pe?.response?.data?.detail;
        // WW-185 · looks abroad: confirm Kenya or keep editing the country.
        if (pe?.response?.status !== 409 || pd?.error !== "outside_kenya_suspected") throw pe;
        if (!(await confirm({ title: "is this in Kenya?", message: pd.message, confirmLabel: "it is in Kenya, save" }))) return;
        r = await client.put(`/admin/events/${event.id}`, { ...patch, confirm_kenya: true });
      }
      toast.success(r.data.no_op ? "nothing to save" : "saved · live updated");
      await onSaved();
    } catch (err) { const dd = err?.response?.data?.detail; toast.error(typeof dd === "string" ? dd : (dd?.message || "save failed")); }
    finally { setSaving(false); }
  };

  const lastEdit = audit[0];

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 60 }} data-testid="published-edit-drawer">
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.55)" }} />
      <aside
        style={{
          position: "absolute", top: 0, right: 0, bottom: 0, width: "min(520px, 100%)",
          background: "var(--card-night)", borderLeft: "1px solid var(--line-night)",
          overflowY: "auto", padding: 24, color: "var(--text-night)", fontFamily: "var(--f-body)",
        }}>
        <div className="flex items-center justify-between">
          <h2 className="text-xl lowercase" style={{ fontFamily: "var(--f-head)", fontWeight: 700 }}>edit published event</h2>
          <button data-testid="published-edit-close" aria-label="close" onClick={onClose} style={{ color: "var(--text-dim-night)" }}><X size={18} /></button>
        </div>

        <label className="block mt-4 text-xs lowercase" style={{ color: "var(--text-muted-night)" }}>
          title
          <input data-testid="published-edit-title" value={form.title} onChange={(e) => field("title", e.target.value)} style={inputStyle} />
        </label>
        <DateFields form={form} field={field} />
        {[
          ["venue_display", "venue"], ["price", "price"], ["ticket_link", "ticket link"],
          ["organiser_handle", "curator handle"], ["co_organiser_handles", "co curator handles (comma)"],
          ["cover_image_url", "cover image url"],
        ].map(([k, lbl]) => (
          <label key={k} className="block mt-4 text-xs lowercase" style={{ color: "var(--text-muted-night)" }}>
            {lbl}
            <input data-testid={`published-edit-${k}`} value={form[k]} onChange={(e) => field(k, e.target.value)} style={inputStyle} />
          </label>
        ))}
        <div className="mt-3 flex items-center gap-3">
          {form.cover_image_url && <PastedCoverPreview url={form.cover_image_url} testid="published-edit-poster-thumb" style={{ width: 56, height: 72, objectFit: "cover", borderRadius: 6 }} />}
          <button data-testid="published-edit-poster" onClick={() => setPosterOpen(true)}
            className="rounded-md border px-3 py-1.5 text-xs font-semibold lowercase"
            style={{ background: "var(--kili)", borderColor: "var(--kili)", color: "#1A1A30", fontFamily: "var(--f-head)" }}>
            edit poster
          </button>
          {posterChanged && <span data-testid="published-edit-poster-state" className="text-[11px] lowercase" style={{ color: posterConfirmed ? "#9FE28F" : "#F8B88B" }}>{posterConfirmed ? "new poster confirmed" : "new poster needs a person to confirm"}</span>}
        </div>
        {posterOpen && <PublishedPosterEditor eventId={event.id} onChange={onPosterChange} onClose={() => setPosterOpen(false)} />}
        <InternetSourcesEditor eventId={event.id} handle={(event.organiser_handle || "").replace(/^@+/, "").toLowerCase()} />
        <label className="block mt-4 text-xs lowercase" style={{ color: "var(--text-muted-night)" }}>
          main iko
          <select data-testid="published-edit-iko" value={form.iko} onChange={(e) => field("iko", e.target.value)}
            style={{ marginTop: 6, display: "block", width: "100%", height: 36, padding: "0 10px", background: "rgba(18,18,42,0.72)", border: "1px solid var(--line-night)", borderRadius: 8, color: "var(--text-night)" }}>
            {IKO_OPTIONS.map((i) => <option key={i} value={i}>{i}</option>)}
          </select>
        </label>
        <div className="mt-4 text-xs lowercase" style={{ color: "var(--text-muted-night)" }}>
          supporting ikos (up to 2)
          <div className="mt-2 flex gap-2">
            {IKO_OPTIONS.filter((i) => i !== form.iko).map((i) => {
              const on = form.iko_supporting.includes(i);
              return (
                <button key={i} data-testid={`published-edit-support-${i}`} type="button"
                  onClick={() => field("iko_supporting", on ? form.iko_supporting.filter((x) => x !== i) : [...form.iko_supporting, i].slice(-2))}
                  className="rounded-md border px-2 py-1 lowercase" style={pill(on)}>{i}</button>
              );
            })}
          </div>
        </div>
        <label className="block mt-4 text-xs lowercase" style={{ color: "var(--text-muted-night)" }}>
          description
          <textarea data-testid="published-edit-description" value={form.description} onChange={(e) => field("description", e.target.value)}
            rows={4} style={{ marginTop: 6, display: "block", width: "100%", padding: 10, background: "rgba(18,18,42,0.72)", border: "1px solid var(--line-night)", borderRadius: 8, color: "var(--text-night)", fontSize: 13, fontFamily: "var(--f-body)" }} />
        </label>

        <div className="mt-6 p-3 rounded-lg" data-testid="published-edit-preview" style={{ background: "rgba(18,18,42,0.42)", border: "1px dashed var(--line-night)" }}>
          <p className="text-[11px] lowercase mb-1" style={{ color: "var(--text-dim-night)" }}>watu will see</p>
          <p className="text-sm" style={{ color: "var(--text-night)" }}>{membersWillSee}</p>
        </div>

        <div className="mt-4" data-testid="published-edit-gates">
          <p className="text-[11px] lowercase mb-1" style={{ color: "var(--text-dim-night)" }}>publish checks</p>
          {gates.length === 0
            ? <p className="text-sm" style={{ color: "#9FE28F" }}>all gates pass</p>
            : <ul className="text-sm" style={{ color: "#F8B88B" }}>{gates.map((g) => <li key={g}>· {g}</li>)}</ul>}
        </div>

        <button data-testid="published-edit-save" onClick={save} disabled={saving || posterBlocked}
          className="mt-6 w-full rounded-md border px-3 py-2 text-sm font-semibold lowercase"
          style={{ background: "var(--kili)", borderColor: "var(--kili)", color: "#1A1A30", fontFamily: "var(--f-head)", opacity: saving || posterBlocked ? 0.6 : 1 }}>
          {saving ? "saving..." : "save and update live"}
        </button>

        <div className="mt-6 pt-4 border-t" style={{ borderColor: "var(--line-night)" }} data-testid="published-edit-audit">
          <p className="text-[11px] lowercase" style={{ color: "var(--text-dim-night)" }}>
            {lastEdit
              ? `edited by ${lastEdit.edited_by_label || lastEdit.edited_by} on ${new Date(lastEdit.edited_at).toLocaleString("en-GB", { timeZone: "Africa/Nairobi" })}`
              : "no edits yet"}
          </p>
          {audit.length > 0 && (
            <ul className="mt-2 text-[11px]" style={{ color: "var(--text-muted-night)" }}>
              {audit.slice(0, 8).map((row, i) => (
                <li key={i} className="mt-1">
                  <span style={{ color: "var(--text-dim-night)" }}>{new Date(row.edited_at).toLocaleDateString("en-GB", { timeZone: "Africa/Nairobi" })} · {row.edited_by_label || row.edited_by}</span>
                  <span className="ml-2">· {Object.keys(row.changes || {}).join(", ")}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </aside>
    </div>
  );
}

// WW-160.9 · same date and time pickers as the review desk, Nairobi time, plus extra dates.
function DateFields({ form, field }) {
  const setSlot = (idx, k, v) => field("slots", form.slots.map((s, i) => (i === idx ? { ...s, [k]: v } : s)));
  const lbl = "block text-xs lowercase";
  return (
    <div data-testid="published-edit-dates">
      <div className="mt-4 grid grid-cols-2 gap-3">
        <label className={lbl} style={{ color: "var(--text-muted-night)" }}>
          start (nairobi time)
          <input type="datetime-local" data-testid="published-edit-start_at" value={form.start_at} onChange={(e) => field("start_at", e.target.value)} style={inputStyle} />
        </label>
        <label className={lbl} style={{ color: "var(--text-muted-night)" }}>
          end (optional, set a later day for a multi day run)
          <input type="datetime-local" data-testid="published-edit-end_at" value={form.end_at} onChange={(e) => field("end_at", e.target.value)} style={inputStyle} />
        </label>
      </div>
      {form.slots.map((s, idx) => (
        <div key={s.id || idx} data-testid={`published-slot-row-${idx}`} className="mt-2 grid grid-cols-2 gap-2 rounded-md border p-2" style={{ borderColor: "var(--line-night)" }}>
          <input placeholder="label (optional)" data-testid={`published-slot-label-${idx}`} value={s.label || ""} onChange={(e) => setSlot(idx, "label", e.target.value)} style={{ ...inputStyle, marginTop: 0 }} />
          <button type="button" data-testid={`published-slot-remove-${idx}`} onClick={() => field("slots", form.slots.filter((_, i) => i !== idx))}
            className="text-xs lowercase underline" style={{ color: "var(--text-dim-night)" }}>remove date</button>
          <input type="datetime-local" data-testid={`published-slot-start-${idx}`} value={s.start_at || ""} onChange={(e) => setSlot(idx, "start_at", e.target.value)} style={{ ...inputStyle, marginTop: 0 }} />
          <input type="datetime-local" data-testid={`published-slot-end-${idx}`} value={s.end_at || ""} onChange={(e) => setSlot(idx, "end_at", e.target.value)} style={{ ...inputStyle, marginTop: 0 }} />
          <input placeholder="price (optional)" data-testid={`published-slot-price-${idx}`} value={s.price || ""} onChange={(e) => setSlot(idx, "price", e.target.value)} style={{ ...inputStyle, marginTop: 0 }} />
          <input placeholder="ticket link (optional)" data-testid={`published-slot-link-${idx}`} value={s.ticket_link || ""} onChange={(e) => setSlot(idx, "ticket_link", e.target.value)} style={{ ...inputStyle, marginTop: 0 }} />
        </div>
      ))}
      <button type="button" data-testid="published-slot-add"
        onClick={() => field("slots", [...form.slots, { id: crypto.randomUUID(), start_at: "", end_at: "", price: "", ticket_link: "", label: "" }])}
        className="mt-2 text-xs font-semibold lowercase hover:underline" style={{ color: "var(--kili)" }}>
        + add another date
      </button>
    </div>
  );
}

